package com.swetdot.swetgram.ui.chats

import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.data.toDomain
import com.swetdot.swetgram.domain.ChatId
import com.swetdot.swetgram.domain.User
import com.swetdot.swetgram.protocol.dto.SearchResponse
import io.ktor.client.request.parameter
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.content.TextContent
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put

/**
 * The conversation-list mutations SPS §10.5-10.7 asks for, plus the people search of §10.4.
 *
 * These are operations the frozen contract already publishes -- `pinConversation`,
 * `archiveConversation`, `deleteConversation`, `globalSearch` -- and which `data/ChatRepository`
 * does not implement: it only lists and opens. Rather than leave pin, archive and delete
 * unreachable in the product, they are issued here, from the screen's own layer, against the same
 * [ApiClient] every repository uses.
 *
 * TODO(integration): this belongs in `data/ChatRepository`, next to `refresh` and
 * `openDirectChat`, with the search half in a `SearchRepository` of its own. It is here only
 * because the change that added it did not own `data/`. Moving it is a file move plus a
 * constructor argument -- nothing in this package reaches past this class.
 *
 * The list is refreshed by the CALLER after a mutation rather than here: every one of these
 * returns 204 with no body, so the cache has no new row to write and the authoritative state
 * still has to come from `listConversations`.
 *
 * MUTE IS DELIBERATELY NOT HERE, and the difference is instructive. `muteConversation` answers
 * `200` with a body -- the deadline -- which is state somebody has to hold, and the same field
 * arrives on every conversation in `listConversations`. So it lives in `data/ChatRepository`, next
 * to the refresh that also reads it, which is where the four below belong too.
 */
class ChatCommands(private val api: ApiClient) {

    /** Contract: max five pinned conversations; a sixth answers `409`. */
    suspend fun pin(id: ChatId) {
        api.post<Unit>("conversations/${id.value}/pin")
    }

    suspend fun unpin(id: ChatId) {
        api.delete<Unit>("conversations/${id.value}/pin")
    }

    suspend fun archive(id: ChatId) {
        api.post<Unit>("conversations/${id.value}/archive")
    }

    suspend fun unarchive(id: ChatId) {
        api.delete<Unit>("conversations/${id.value}/archive")
    }

    /**
     * SPS §10.7's recommended v1 behaviour: "delete for current user only, not for all
     * participants". `scope: everyone` exists in the contract and is deliberately not offered
     * here -- it destroys the other person's copy too, and that needs its own three-way dialog.
     *
     * The body is built as a [TextContent] rather than handed to content negotiation, because
     * there is no request DTO for it in `:protocol` and inventing one in `:shared` would put a
     * wire type on the wrong side of IMPLEMENTATION_NOTES §10.
     */
    suspend fun deleteForMe(id: ChatId) {
        api.post<Unit>("conversations/${id.value}/deletion") {
            setBody(
                TextContent(
                    buildJsonObject { put("scope", "self") }.toString(),
                    ContentType.Application.Json,
                ),
            )
        }
    }

    /**
     * SPS §10.4 and §14.1: people by username and by display name.
     *
     * Only the `people` array is read. Groups have no screen in this build, and the
     * `conversations` array is redundant here -- the chat list already holds every conversation
     * the user has, and filtering that locally answers instantly and works offline.
     */
    suspend fun searchPeople(query: String): List<User> {
        val response: SearchResponse = api.get("search") {
            parameter("q", query)
            parameter("limit", SEARCH_LIMIT)
        }
        return response.people.map { it.toDomain() }
    }

    private companion object {
        /** Enough to fill a phone screen; more would just be scrolled past. */
        const val SEARCH_LIMIT = 20
    }
}
